import React, { useState } from "react";
import { X, Plus } from "lucide-react";
import JsonView from "./JsonView";

/**
 * ToolsFormView - Form interface for tools.json editing
 * 
 * Handles two agent types:
 * - Expert: {"tools": ["tool1", "tool2"]} - MCP server tool names
 * - Swarm: {"agents": ["agent1", "agent2"]} - Other agent IDs to delegate to
 * 
 * @param {Object} props
 * @param {string} props.value - JSON string of tools.json content
 * @param {Function} props.onChange - Callback(jsonString: string) when form data changes
 * @param {Array<{agent_id: string, name: string, description: string}>} props.availableAgents - For swarm selection
 * @param {string} props.currentAgentId - Current agent ID to prevent self-reference
 * @param {boolean} props.readOnly - Disable editing
 * 
 * State Management:
 * - agentType: 'expert' | 'swarm' (detected from JSON or user selection)
 * - toolsList: string[] for expert agents
 * - selectedAgents: string[] for swarm agents
 * 
 * Sync Logic:
 * - Parse value on mount/change -> populate form state
 * - Form changes -> build JSON -> call onChange with stringified result
 * - No inline validation - parent validates on save
 */
const ToolsFormView = ({
  value,
  onChange,
  availableAgents = [],
  currentAgentId = null,
  readOnly = false,
  formState,
  onFormStateChange,
  mode = "form",
  onModeSwitch,
  supportsFormView = true,
  onError,
  fileType = "tools"
}) => {
  const [toolInput, setToolInput] = useState("");

  // Use lifted state from parent (preserved across view switches)
  const agentType = formState?.agentType || "expert";
  const expertTools = formState?.expertTools || [];
  const swarmAgents = formState?.swarmAgents || [];
  
  const setAgentType = (newType) => {
    if (onFormStateChange) {
      onFormStateChange({
        ...formState,
        agentType: newType
      });
    }
  };
  
  const setExpertTools = (tools) => {
    if (onFormStateChange) {
      onFormStateChange({
        ...formState,
        expertTools: tools
      });
    }
  };
  
  const setSwarmAgents = (agents) => {
    if (onFormStateChange) {
      onFormStateChange({
        ...formState,
        swarmAgents: agents
      });
    }
  };

  // Build JSON and notify parent (no validation)
  const syncToParent = (type, list) => {
    const json = type === "expert" 
      ? { tools: list }
      : { agents: list };
    
    const jsonString = JSON.stringify(json, null, 2);
    
    if (onChange) {
      onChange(jsonString);
    }
  };

  // Handle agent type change - preserves both states
  const handleAgentTypeChange = (newType) => {
    setAgentType(newType);
    
    // Sync the newly selected type to parent (keeps both states in memory)
    if (newType === "expert") {
      syncToParent("expert", expertTools);
    } else {
      syncToParent("swarm", swarmAgents);
    }
  };

  // Expert Agent - Add tool
  const handleAddTool = () => {
    const trimmed = toolInput.trim();
    if (!trimmed) return;
    
    if (expertTools.includes(trimmed)) {
      return; // Silently ignore duplicates
    }
    
    const newList = [...expertTools, trimmed];
    setExpertTools(newList);
    setToolInput("");
    syncToParent("expert", newList);
  };

  // Expert Agent - Remove tool
  const handleRemoveTool = (tool) => {
    const newList = expertTools.filter(t => t !== tool);
    setExpertTools(newList);
    syncToParent("expert", newList);
  };

  // Swarm Agent - Toggle agent selection
  const handleToggleAgent = (agentId) => {
    const newList = swarmAgents.includes(agentId)
      ? swarmAgents.filter(a => a !== agentId)
      : [...swarmAgents, agentId];
    
    setSwarmAgents(newList);
    syncToParent("swarm", newList);
  };

  return (
    <div className="space-y-4">
      {/* Agent Type Selector - Always visible */}
      <div className="pb-4 border-b border-edge-gray-200 dark:border-edge-gray-700">
        <div className="grid grid-cols-2 gap-4">
          <label className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-all ${
            agentType === "expert" 
              ? "border-edge-primary bg-fill-primary/5 shadow-md"
              : "border-edge-gray-200 dark:border-edge-gray-700 hover:border-edge-gray-300 dark:hover:border-edge-gray-600 shadow-sm"
          }`}>
            <input
              type="radio"
              value="expert"
              checked={agentType === "expert"}
              onChange={(e) => handleAgentTypeChange(e.target.value)}
              disabled={readOnly}
              className="w-4 h-4 mt-0.5 text-ink-primary focus:ring-focus-primary"
            />
            <div>
              <div className="text-sm font-semibold text-ink-gray-900 dark:text-ink-white">Expert Agent</div>
              <div className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">MCP Tools</div>
            </div>
          </label>
          <label className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-all ${
            agentType === "swarm" 
              ? "border-edge-primary bg-fill-primary/5 shadow-md"
              : "border-edge-gray-200 dark:border-edge-gray-700 hover:border-edge-gray-300 dark:hover:border-edge-gray-600 shadow-sm"
          }`}>
            <input
              type="radio"
              value="swarm"
              checked={agentType === "swarm"}
              onChange={(e) => handleAgentTypeChange(e.target.value)}
              disabled={readOnly}
              className="w-4 h-4 mt-0.5 text-ink-primary focus:ring-focus-primary"
            />
            <div>
              <div className="text-sm font-semibold text-ink-gray-900 dark:text-ink-white">Swarm Agent</div>
              <div className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">Distributed Agents</div>
            </div>
          </label>
        </div>
      </div>

      {/* Content Section with Toggle */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-semibold text-ink-gray-700 dark:text-ink-gray-300">
            {mode === "form" ? (agentType === "expert" ? "Tool Filter (optional)" : "Agent Filter") : "JSON Editor"}
          </label>
          
          {/* View Mode Toggle */}
          {supportsFormView && (
            <div className="flex items-center gap-0.5 bg-fill-gray-100 dark:bg-fill-gray-800 rounded-md p-0.5">
              <button
                type="button"
                onClick={() => onModeSwitch("form")}
                disabled={mode === "form"}
                className={`px-2 py-1 text-sm font-mono rounded transition-all ${
                  mode === "form"
                    ? "bg-fill-white dark:bg-fill-gray-700 text-ink-primary shadow-sm"
                    : "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-gray-700 dark:hover:text-ink-gray-300"
                }`}
                title="Form View"
              >
                [form]
              </button>
              <button
                type="button"
                onClick={() => onModeSwitch("json")}
                disabled={mode === "json"}
                className={`px-2 py-1 text-sm font-mono rounded transition-all ${
                  mode === "json"
                    ? "bg-fill-white dark:bg-fill-gray-700 text-ink-primary shadow-sm"
                    : "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-gray-700 dark:hover:text-ink-gray-300"
                }`}
                title="JSON View"
              >
                {"{json}"}
              </button>
            </div>
          )}
        </div>

        {/* Conditional Content: Form or JSON */}
        {mode === "json" ? (
          <JsonView
            value={value}
            onChange={onChange}
            onError={onError}
            fileType={fileType}
            readOnly={readOnly}
          />
        ) : (
          <>
            {/* Expert Agent Form */}
            {agentType === "expert" && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={toolInput}
                    onChange={(e) => setToolInput(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddTool()}
                    placeholder="e.g., read_file, write_file"
                    disabled={readOnly}
                    className="photon-field flex-1 px-4 py-2 border border-edge-gray-300 dark:border-edge-gray-600 rounded-lg bg-fill-white dark:bg-fill-deep-surface text-ink-ink dark:text-ink-white focus:ring-2 focus:ring-focus-primary focus:border-transparent text-sm"
                  />
                  <button
                    type="button"
                    onClick={handleAddTool}
                    disabled={!toolInput.trim() || readOnly}
                    className="px-4 py-2 bg-fill-primary text-ink-on-primary rounded-lg hover:bg-fill-primary-hover-muted disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add
                  </button>
                </div>
                <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
                  Leave empty to allow all tools from MCP server
                </p>

                {/* Tools List */}
                {expertTools.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {expertTools.map((tool) => (
                      <div
                        key={tool}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-fill-primary text-ink-on-primary rounded-lg text-sm font-medium shadow-sm"
                      >
                        <span>{tool}</span>
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={() => handleRemoveTool(tool)}
                            aria-label={`Remove ${tool}`}
                            className="text-ink-white/80 hover:text-ink-red-300"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Swarm Agent Form */}
            {agentType === "swarm" && (
              <div>
                {(() => {
                  // Filter out current agent to prevent self-reference
                  const filteredAgents = currentAgentId 
                    ? availableAgents.filter(agent => agent.agent_id !== currentAgentId)
                    : availableAgents;
                  
                  return filteredAgents.length === 0 ? (
                    <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 py-4">
                      {currentAgentId && availableAgents.length > 0
                        ? "No other agents available. Agents cannot delegate to themselves."
                        : "No agents available. Create expert agents first."}
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {filteredAgents.map((agent) => (
                      <label
                        key={agent.agent_id}
                        className="flex items-start gap-3 p-3 border border-edge-gray-200 dark:border-edge-gray-700 rounded-lg hover:bg-fill-gray-50 dark:hover:bg-fill-gray-800 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={swarmAgents.includes(agent.agent_id)}
                          onChange={() => handleToggleAgent(agent.agent_id)}
                          disabled={readOnly}
                          className="w-4 h-4 mt-0.5 text-ink-primary rounded focus:ring-focus-primary"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-ink-gray-700 dark:text-ink-gray-300">
                            {agent.name}
                          </div>
                          <div className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
                            @{agent.agent_id}
                          </div>
                          {agent.description && (
                            <div className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-1">
                              {agent.description}
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ToolsFormView;
